import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Info,
  Cpu,
  Activity,
  History,
  Zap,
  CheckCircle2,
  XCircle,
  Clock,
  Play,
  Layers,
  AlertCircle,
  TrendingUp,
} from 'lucide-react';
import { Card } from '../ui/Card';
import { agentsService, type AgentDetails, type AgentRun, type AgentStats } from '@/services/agents';
import { skillService, type Skill } from '@/services/skills';
import { toast } from 'sonner';

type Tab = 'overview' | 'skills' | 'runs' | 'stats';

interface AgentDetailsDrawerProps {
  agentId: string | null;
  open: boolean;
  onClose: () => void;
  onRunSkill?: (skill: Skill) => Promise<void> | void;
  onAgentsChanged?: () => void;
}

const TAB_LABELS: Array<{ id: Tab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: 'overview', label: 'Overview', icon: Info },
  { id: 'skills', label: 'Skills', icon: Layers },
  { id: 'runs', label: 'Recent Runs', icon: History },
  { id: 'stats', label: 'Stats', icon: TrendingUp },
];

function statusDotClass(status: string): string {
  const s = (status || '').toLowerCase();
  if (s === 'active' || s === 'acting' || s === 'thinking') return 'bg-blue-400 animate-pulse';
  if (s === 'error' || s === 'failed') return 'bg-red-500';
  return 'bg-khiimori-bg-tertiary';
}

function runStatusBadge(status: string): string {
  const s = (status || '').toLowerCase();
  if (s === 'success') return 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30';
  if (s === 'failed') return 'bg-red-500/15 text-red-300 border border-red-500/30';
  if (s === 'skipped') return 'bg-amber-500/15 text-amber-300 border border-amber-500/30';
  return 'bg-khiimori-bg-elevated/30 text-khiimori-text-secondary border border-khiimori-border/20';
}

function formatRelative(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return `${Math.max(0, diffSec)}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function formatAvgResponse(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 10) return `${seconds.toFixed(1)}s`;
  return `${Math.round(seconds)}s`;
}

function StatsCards({ stats }: { stats: AgentStats }) {
  const cards = [
    { label: 'Total runs', value: stats.total_runs, icon: <Activity className="w-4 h-4" />, color: 'text-blue-400' },
    {
      label: 'Success rate',
      value: `${Math.round((stats.success_rate || 0) * 100)}%`,
      icon: <CheckCircle2 className="w-4 h-4" />,
      color: 'text-emerald-400',
    },
    {
      label: 'Avg response',
      value: formatAvgResponse(stats.avg_response_time_ms || 0),
      icon: <Clock className="w-4 h-4" />,
      color: 'text-amber-400',
    },
    { label: 'Last run', value: formatRelative(stats.last_run_at), icon: <History className="w-4 h-4" />, color: 'text-violet-400' },
  ];
  return (
    <div className="grid grid-cols-2 gap-2">
      {cards.map((c, i) => (
        <Card key={i} className="p-3" padding="sm">
          <div className="flex items-center gap-2 mb-1 text-khiimori-text-muted text-[10px] uppercase tracking-wider">
            <span className={c.color}>{c.icon}</span>
            <span>{c.label}</span>
          </div>
          <div className="text-base font-semibold text-khiimori-text-primary">{c.value}</div>
        </Card>
      ))}
    </div>
  );
}

function OverviewTab({ details }: { details: AgentDetails }) {
  return (
    <div className="space-y-3">
      <div className="p-4 rounded-xl bg-khiimori-bg-elevated/5 border border-khiimori-border/10">
        <h4 className="text-xs font-semibold text-khiimori-text-muted uppercase tracking-wider mb-2">Description</h4>
        <p className="text-sm text-khiimori-text-secondary leading-relaxed">{details.description || 'No description available.'}</p>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <Card className="p-3" padding="sm">
          <p className="text-[10px] uppercase tracking-wider text-khiimori-text-muted">Type</p>
          <p className="font-semibold text-khiimori-text-primary capitalize">{details.type || '—'}</p>
        </Card>
        <Card className="p-3" padding="sm">
          <p className="text-[10px] uppercase tracking-wider text-khiimori-text-muted">Capabilities</p>
          <p className="font-semibold text-khiimori-text-primary">{details.capabilities?.length || 0}</p>
        </Card>
        <Card className="p-3" padding="sm">
          <p className="text-[10px] uppercase tracking-wider text-khiimori-text-muted">Skills owned</p>
          <p className="font-semibold text-khiimori-text-primary">{details.skills?.length || 0}</p>
        </Card>
        <Card className="p-3" padding="sm">
          <p className="text-[10px] uppercase tracking-wider text-khiimori-text-muted">Last activity</p>
          <p className="font-semibold text-khiimori-text-primary">{formatRelative(details.last_activity)}</p>
        </Card>
      </div>
    </div>
  );
}

function SkillsTab({
  details,
  onRunSkill,
}: {
  details: AgentDetails;
  onRunSkill?: (skill: Skill) => Promise<void> | void;
}) {
  const skills = details.skills || [];
  if (skills.length === 0) {
    return (
      <p className="text-sm text-khiimori-text-muted text-center py-8">
        This agent has no skills registered yet.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {skills.map((s) => (
        <div
          key={s.id}
          className="p-3 rounded-xl bg-khiimori-bg-elevated/5 border border-khiimori-border/10 flex items-start gap-3"
        >
          <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 flex-shrink-0">
            <Zap className="w-3.5 h-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h5 className="text-sm font-semibold text-khiimori-text-primary truncate">{s.name}</h5>
              <span className="text-[10px] font-mono text-khiimori-text-muted">{s.id}</span>
            </div>
            <p className="text-xs text-khiimori-text-muted line-clamp-2 mt-0.5">{s.description}</p>
          </div>
          <button
            type="button"
            onClick={async () => {
              if (onRunSkill) {
                await onRunSkill(s);
                return;
              }
              try {
                const res = await skillService.executeSkill(s.id, {});
                if (res.status === 'success') toast.success(`Ran "${s.name}"`);
                else if (res.status === 'skipped') toast.message(res.message || 'Deep-link only');
                else toast.error(`Failed: ${res.message || s.name}`);
              } catch (e) {
                toast.error(`Failed to run ${s.name}: ${(e as Error).message}`);
              }
            }}
            className="p-1.5 rounded-md text-emerald-400 hover:bg-emerald-500/10 transition-colors flex-shrink-0"
            title={`Run ${s.name}`}
            aria-label={`Run ${s.name}`}
          >
            <Play className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

function RunsTab({ runs, loading }: { runs: AgentRun[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-14 rounded-xl bg-khiimori-bg-elevated/10 animate-pulse" />
        ))}
      </div>
    );
  }
  if (runs.length === 0) {
    return (
      <p className="text-sm text-khiimori-text-muted text-center py-8">
        No runs recorded yet for this agent.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {runs.map((r) => (
        <div
          key={r.run_id}
          className="p-3 rounded-xl bg-khiimori-bg-elevated/5 border border-khiimori-border/10 flex items-start gap-3"
        >
          <div className="pt-0.5">
            {r.status === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            ) : r.status === 'failed' ? (
              <XCircle className="w-4 h-4 text-red-400" />
            ) : (
              <Clock className="w-4 h-4 text-amber-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <span className="text-sm font-medium text-khiimori-text-primary truncate">{r.skill_id || r.run_id}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${runStatusBadge(r.status)}`}>{r.status || 'unknown'}</span>
            </div>
            {r.message && <p className="text-xs text-khiimori-text-muted line-clamp-2">{r.message}</p>}
            <p className="text-[10px] text-khiimori-text-muted mt-1">{formatRelative(r.executed_at)}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export const AgentDetailsDrawer: React.FC<AgentDetailsDrawerProps> = ({
  agentId,
  open,
  onClose,
  onRunSkill,
  onAgentsChanged,
}) => {
  const [details, setDetails] = useState<AgentDetails | null>(null);
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [tab, setTab] = useState<Tab>('overview');
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetails = useCallback(async (id: string) => {
    setLoadingDetails(true);
    setError(null);
    try {
      const d = await agentsService.details(id);
      if (!d) {
        setError('Agent not found');
        setDetails(null);
        return;
      }
      setDetails(d);
      setRuns(d.recent_runs || []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  const fetchRuns = useCallback(async (id: string) => {
    setLoadingRuns(true);
    try {
      const r = await agentsService.runs(id, 20);
      setRuns(r);
    } catch {
      setRuns([]);
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  useEffect(() => {
    if (open && agentId) {
      setTab('overview');
      void fetchDetails(agentId);
    } else {
      setDetails(null);
      setRuns([]);
      setError(null);
    }
  }, [open, agentId, fetchDetails]);

  useEffect(() => {
    if (!open || !agentId) return;
    if (tab === 'runs') {
      void fetchRuns(agentId);
    } else if (tab === 'overview' && details) {
      setRuns(details.recent_runs || []);
    }
  }, [tab, open, agentId, details, fetchRuns]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleRunAll = async () => {
    if (!details || !details.skills || details.skills.length === 0) {
      toast.message('No skills to run');
      return;
    }
    let ok = 0;
    for (const s of details.skills) {
      if (s.frontend_route) continue; // skip deep-link-only
      try {
        const res = await skillService.executeSkill(s.id, {});
        if (res.status === 'success') ok++;
      } catch {
        /* ignore */
      }
    }
    toast.success(`Ran ${ok} of ${details.skills.length} owned skills`);
    if (agentId) {
      void fetchDetails(agentId);
      void fetchRuns(agentId);
    }
    onAgentsChanged?.();
  };

  const handleRunOne = async (skill: Skill) => {
    if (onRunSkill) {
      await onRunSkill(skill);
    } else {
      try {
        const res = await skillService.executeSkill(skill.id, {});
        if (res.status === 'success') toast.success(`Ran "${skill.name}"`);
        else if (res.status === 'skipped') toast.message(res.message || 'Deep-link only');
        else toast.error(`Failed: ${res.message || skill.name}`);
      } catch (e) {
        toast.error(`Failed to run ${skill.name}: ${(e as Error).message}`);
        return;
      }
    }
    if (agentId) {
      void fetchDetails(agentId);
      void fetchRuns(agentId);
    }
    onAgentsChanged?.();
  };

  return (
    <AnimatePresence>
      {open && agentId && (
        <>
          <motion.div
            key="agent-details-overlay"
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.aside
            key="agent-details-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.24, ease: 'easeOut' }}
            className="fixed right-0 top-0 z-50 h-full w-full sm:w-[420px] bg-khiimori-bg-elevated/10 backdrop-blur-xl border-l border-khiimori-border/20 shadow-glass flex flex-col"
            role="dialog"
            aria-label="Agent details"
          >
            <header className="p-4 border-b border-khiimori-border/10 flex items-start gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex-shrink-0">
                <Cpu className="w-5 h-5 text-khiimori-text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-bold text-khiimori-text-primary truncate">
                    {details?.name || (loadingDetails ? 'Loading…' : 'Agent')}
                  </h2>
                  {details && (
                    <span className="flex items-center gap-1.5 text-[10px] px-1.5 py-0.5 rounded bg-khiimori-bg-elevated/30 text-khiimori-text-secondary">
                      <span className={`w-1.5 h-1.5 rounded-full ${statusDotClass(details.status)}`} />
                      {details.status}
                    </span>
                  )}
                </div>
                {details && (
                  <p className="text-xs text-khiimori-text-muted mt-0.5 font-mono truncate">{details.id}</p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-md text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/30"
                aria-label="Close drawer"
              >
                <X className="w-4 h-4" />
              </button>
            </header>

            {error && (
              <div className="m-4 p-3 rounded-xl border border-red-500/30 bg-red-500/10 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-300">{error}</p>
              </div>
            )}

            <div className="flex items-center gap-1.5 px-4 py-2 border-b border-khiimori-border/10 overflow-x-auto">
              {TAB_LABELS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                    tab === id
                      ? 'bg-blue-600 text-white'
                      : 'bg-khiimori-bg-elevated/20 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/40'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loadingDetails && !details ? (
                <div className="space-y-3">
                  <div className="h-20 rounded-xl bg-khiimori-bg-elevated/10 animate-pulse" />
                  <div className="h-32 rounded-xl bg-khiimori-bg-elevated/10 animate-pulse" />
                </div>
              ) : details ? (
                <>
                  {tab === 'overview' && <OverviewTab details={details} />}
                  {tab === 'skills' && <SkillsTab details={details} onRunSkill={handleRunOne} />}
                  {tab === 'runs' && <RunsTab runs={runs} loading={loadingRuns} />}
                  {tab === 'stats' && <StatsCards stats={details.stats} />}
                </>
              ) : null}
            </div>

            {details && (
              <footer className="p-4 border-t border-khiimori-border/10 flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleRunAll}
                  disabled={(details.skills || []).length === 0}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Zap className="w-3.5 h-3.5" />
                  Run all owned skills
                </button>
                <button
                  type="button"
                  onClick={() => agentId && fetchDetails(agentId)}
                  className="px-3 py-2 rounded-lg bg-khiimori-bg-elevated/20 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/40 transition-colors text-sm"
                  aria-label="Refresh"
                  title="Refresh"
                >
                  <Activity className="w-3.5 h-3.5" />
                </button>
              </footer>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default AgentDetailsDrawer;
